/**
 * EQuIP Role Definitions
 *
 * Role names must match Azure AD App Roles configured in the
 * Keystone Platform Auth App Registration.
 *
 * IMPORTANT: The synced file equip-utils.ts (from Keystone Platform)
 * depends on these exact ROLES values using dot-notation strings.
 * If you change these values, you must also update Keystone's roles.ts.
 * The canonical source of truth is Keystone Platform's roles.ts.
 */

export const ROLES = {
  // Global admin (from Keystone)
  ADMIN: 'Admin',

  // EQuIP-specific roles (dot-notation matches Azure AD App Roles)
  EQUIP_READ: 'equip.read', // Read-only EQuIP access (clinician view)
  EQUIP_ADMIN: 'equip.admin', // Full EQuIP access
  EQUIP_PHYSICIAN_LEAD: 'equip.physician_lead', // Physician track lead
  EQUIP_APC_LEAD: 'equip.apc_lead', // APC/Non-Physician track lead
  EQUIP_SENIOR_LEAD: 'equip.senior_lead', // Senior leadership (cross-track visibility)
  EQUIP_CSS: 'equip.css', // CSS team (admin view, no award visibility)
} as const

export type RoleName = (typeof ROLES)[keyof typeof ROLES]
export type NavPermission = RoleName | RoleName[] | 'authenticated'

// Role groups for permission checks
export const EQUIP_LEADS = [ROLES.EQUIP_PHYSICIAN_LEAD, ROLES.EQUIP_APC_LEAD, ROLES.EQUIP_SENIOR_LEAD] as RoleName[]
export const EQUIP_ALL = [ROLES.EQUIP_ADMIN, ...EQUIP_LEADS] as RoleName[]

// Roles that can access physician track
export const PHYSICIAN_TRACK_ROLES = [
  ROLES.ADMIN,
  ROLES.EQUIP_ADMIN,
  ROLES.EQUIP_PHYSICIAN_LEAD,
] as RoleName[]

// Roles that can access APC/non-physician track
export const APC_TRACK_ROLES = [ROLES.ADMIN, ROLES.EQUIP_ADMIN, ROLES.EQUIP_APC_LEAD, ROLES.EQUIP_SENIOR_LEAD] as RoleName[]

// Roles that can access EQuIP performance views (clinician + leadership + CSS)
export const EQUIP_PERFORMANCE_ROLES = [
  ROLES.EQUIP_READ,
  ROLES.EQUIP_ADMIN,
  ROLES.EQUIP_CSS,
  ROLES.EQUIP_PHYSICIAN_LEAD,
  ROLES.EQUIP_APC_LEAD,
  ROLES.EQUIP_SENIOR_LEAD,
] as RoleName[]

// Roles that can access EQuIP content pages (framework, guides, FAQ) — excludes CSS
export const EQUIP_CONTENT_ROLES = [
  ROLES.EQUIP_READ,
  ROLES.EQUIP_ADMIN,
  ROLES.EQUIP_PHYSICIAN_LEAD,
  ROLES.EQUIP_APC_LEAD,
  ROLES.EQUIP_SENIOR_LEAD,
] as RoleName[]

/**
 * Check if a user has only equip.read access (no leader or admin roles).
 * Used to redirect clinician-only users straight to /performance.
 */
export function isEquipReadOnly(roles: string[]): boolean {
  return (
    roles.includes(ROLES.EQUIP_READ) &&
    !roles.includes(ROLES.ADMIN) &&
    !roles.includes(ROLES.EQUIP_ADMIN) &&
    !roles.includes(ROLES.EQUIP_PHYSICIAN_LEAD) &&
    !roles.includes(ROLES.EQUIP_APC_LEAD) &&
    !roles.includes(ROLES.EQUIP_SENIOR_LEAD)
  )
}

/**
 * Check if a user has only equip.css access (no lead or admin roles).
 * Used to redirect CSS staff straight to /performance (dashboard only).
 */
export function isEquipCSSOnly(roles: string[]): boolean {
  return (
    roles.includes(ROLES.EQUIP_CSS) &&
    !roles.includes(ROLES.ADMIN) &&
    !roles.includes(ROLES.EQUIP_ADMIN) &&
    !roles.includes(ROLES.EQUIP_PHYSICIAN_LEAD) &&
    !roles.includes(ROLES.EQUIP_APC_LEAD) &&
    !roles.includes(ROLES.EQUIP_SENIOR_LEAD)
  )
}
